import React, { useCallback, useEffect, useRef, useState } from 'react';
import { get, isNaN, orderBy } from 'lodash';
import { useFieldArray } from 'react-hook-form';
import {
  ATMButton,
  ATMDatePicker,
  ATMDropdown,
  ATMField,
  ATMGrid,
  ATMHeader,
  ATMInput,
  ATMTable,
} from 'shared-it-appmod-ui';
import FieldReadOnly from 'src/components/atoms/field/field-readonly/field-readonly.component';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import { RequestFlightPathName } from 'src/constants';
import Lang from 'src/libraries/language';
import { IRequestAdminFormMethods } from 'src/models/request.model';
import { getFlightTypeStatus } from 'src/selectors/flight-type.selector';
import { actionTypes } from 'src/ducks/flight-operator.duck';
import { actionTypes as flightTypeActionTypes } from 'src/ducks/flight-type.duck';
import { useFlightOperatorContext } from 'src/contexts/flight-operator.context';
import { useFlightTypeContext } from 'src/contexts/flight-type.context';
import {
  IFlightPilot,
  IFlightTailNumber,
} from 'src/models/flight-operator.model';
import { getFlightOperatorStatus } from 'src/selectors/flight-operator.selector';
import { ToastSuccess } from 'src/components/atoms/toaster/toaster.component';
import { Confirm } from 'semantic-ui-react';
import styles from './flight-request-admin.module.scss';

type IProps = IRequestAdminFormMethods & {
  name: string;
  limit?: number;
};

const FlightRequestMultiFlightInfo: React.FC<IProps> = ({ control, errors, name, limit }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const { state, actions } = useFlightOperatorContext();
  // const [showTable, setShowTable] = useState(false);
  const [deleteMultiFlightItem, setMultiFlightItem] = useState<any>();
  const { state: flightTypeState, actions: flightTypeActions } =
    useFlightTypeContext();
  const FlightCategoryDrone = ['droneInternal', 'droneExternal'];
  const [pilotInCommand, setPilotInCommand] = useState<IFlightPilot[][]>([]);
  const [tailNumbers, setTailNumbers] = useState<IFlightTailNumber[][]>([]);

  const riskScore = control.defaultValuesRef?.current?.request?.riskScore;
  const droneFlight =
    control.defaultValuesRef?.current?.droneFlightContractor ||
    control.defaultValuesRef?.current?.droneFlightSDGE;

  type flightInfoFields = {
    multiFlightInfoId: string;
    requestId: string;
    flightNo: string;
    flightDate: string;
    departureTime: string;
    arrivalTime: string;
    flightType: string;
    operatorId: string;
    operatorName: string;
    tailNumber: string;
    pilot: string;
    standByHours?: string;
    flightHours?: string;
    landingAssured?: string;
    flightCost?: string;
    riskScore?: string;
  };

  const bufferRef = useRef<flightInfoFields[]>([]);
  // Keep buffer in sync with live form values
  useEffect(() => {
    bufferRef.current = fields as flightInfoFields[];
  }, [fields]);

  // Get next unique flight number
  const nextFlightNo = useCallback(() => {
    const baseFlightNo =
      control.defaultValuesRef.current?.flightInfo?.flightNo || '';
    const allFlights = bufferRef.current;

    let suffix = 1;
    let newFlightNo = `${baseFlightNo} - ${suffix.toString().padStart(2, '0')}`;
    const existingFlightNos = new Set(allFlights.map((f) => f.flightNo));

    // Keep incrementing until we find a unique one
    while (existingFlightNos.has(newFlightNo)) {
      suffix += 1;
      newFlightNo = `${baseFlightNo} - ${suffix.toString().padStart(2, '0')}`;
    }

    return baseFlightNo ? newFlightNo : '';
  }, [control, name]);

  const handleAdd = useCallback(() => {
    // setShowTable(true);
    const currentFlights = bufferRef.current.length
      ? bufferRef.current
      : control.defaultValuesRef?.current?.[name] || [];

    const newFlightInfo: flightInfoFields = {
      multiFlightInfoId: '',
      requestId: '',
      flightNo: nextFlightNo(),
      flightDate: '',
      departureTime: '',
      arrivalTime: '',
      flightType: '',
      operatorId: '',
      operatorName: '',
      tailNumber: '',
      pilot: '',
    };

    if (!droneFlight) {
      newFlightInfo.standByHours = '';
      newFlightInfo.flightHours = '';
      newFlightInfo.landingAssured = '';
      newFlightInfo.flightCost = '';
      newFlightInfo.riskScore = '';
    }

    const updatedData = [...currentFlights, newFlightInfo];
    bufferRef.current = updatedData;

    // eslint-disable-next-line no-param-reassign
    control.defaultValuesRef.current[name] = updatedData;

    append(newFlightInfo);
  }, [append, control, droneFlight, name, nextFlightNo]);

  // useEffect(() => {
  //   if (!fields.length) {
  //     handleAdd();
  //   }
  // }, [fields, handleAdd]);

  useEffect(() => {
    // Get the request flight date as default value if flight info flight date is empty
    if (!get(control, 'defaultValuesRef.current.flightInfo.flightDate')) {
      control.setValue(
        'flightInfo.flightDate',
        get(control, 'defaultValuesRef.current.request.flightDate')
      );
    }
    if (!get(control, 'defaultValuesRef.current.flightInfo.flightType')) {
      if (control.defaultValuesRef.current.passengerFlight) {
        control.setValue(
          'flightInfo.flightType',
          get(control, 'defaultValuesRef.current.passengerFlight.flightType')
        );
      }
      if (control.defaultValuesRef.current.passengerContractorFlight) {
        control.setValue(
          'flightInfo.flightType',
          get(
            control,
            'defaultValuesRef.current.passengerContractorFlight.flightType'
          )
        );
      }
    }
  }, [control, flightTypeState]);
  useEffect(() => {
    return () => {
      window.location.reload();
    };
  }, [riskScore]);

  useEffect(() => {
    actions.listGET(true);
    flightTypeActions.listGET();
  }, [actions, flightTypeActions]);

  const handleChange = useCallback(
    (value, i) => {
      const operator = state.list.find((item) => value === item.name);

      const updatedPilotInCommand = [...pilotInCommand];
      const updatedTailNumbers = [...tailNumbers];

      updatedPilotInCommand[i] = orderBy(
        operator?.Pilots ?? [],
        ['name'],
        ['asc']
      );
      updatedTailNumbers[i] = orderBy(
        operator?.TailNumbers ?? [],
        ['tailNumber'],
        ['asc']
      );

      setPilotInCommand(updatedPilotInCommand);
      setTailNumbers(updatedTailNumbers);

      control.setValue(
        `multiFlightInfo[${i}].operatorId`,
        operator?.operatorId
      );
      control.setValue(
        `multiFlightInfo[${i}].pilot`,
        control.getValues(`multiFlightInfo[${i}].pilot`) || undefined
      );
      control.setValue(
        `multiFlightInfo[${i}].tailNumber`,
        control.getValues(`multiFlightInfo[${i}].tailNumber`) || undefined
      );
    },
    [state, control, pilotInCommand, tailNumbers]
  );

  useEffect(() => {
    const initialPilotInCommand = fields.map((field, i) => {
      const value = get(
        control,
        `defaultValuesRef.current.multiFlightInfo[${i}].operatorId`
      );
      if (value && state.list.length) {
        const list = state.list.find((item) => value === item.operatorId);
        return orderBy(list?.Pilots ?? [], ['name'], ['asc']);
      }
      return [];
    });

    const initialTailNumbers = fields.map((field, i) => {
      const value = get(
        control,
        `defaultValuesRef.current.multiFlightInfo[${i}].operatorId`
      );
      if (value && state.list.length) {
        const list = state.list.find((item) => value === item.operatorId);
        return orderBy(list?.TailNumbers ?? [], ['tailNumber'], ['asc']);
      }
      return [];
    });

    setPilotInCommand(initialPilotInCommand);
    setTailNumbers(initialTailNumbers);
  }, [state.list, control, fields]);

  const status = getFlightOperatorStatus(
    state,
    actionTypes.FLIGHT_OPERATOR_FETCH
  );

  const flightTypeStatus = getFlightTypeStatus(
    flightTypeState,
    flightTypeActionTypes.FLIGHT_TYPE_FETCH
  );

  return (
    <>
      <ATMGrid>
        <ATMGrid.Column>
          <ATMHeader size="small" className="text-underline">
            {Lang.TTL_ADDITIONAL_MULTI_FLIGHT_DETAILS}
          </ATMHeader>
          <ATMTable>
          {/* {showTable && ( */}
            <ATMTable.Body>
              {fields.map((item, i) => {
                const elementId = item.id ?? i.toString();

                return (
                  <ATMTable.Row key={elementId}>
                    <ATMTable.Cell>
                      {item.multiFlightInfoId && (
                        <ATMField
                          key={`multiFlightInfoId.multiFlightInfo_${elementId}`}
                          name={`multiFlightInfo[${i}].multiFlightInfoId`}
                          defaultValue={item.multiFlightInfoId}
                          className="hidden"
                          as={ATMInput}
                          control={control}
                          type="hidden"
                        />
                      )}
                    </ATMTable.Cell>
                    <ATMHeader size="small" className="text-underline">
                      {Lang.TTL_ADDITIONAL_FLIGHT_DATE_TIME}-{i + 1}
                    </ATMHeader>

                    <ATMGrid columns={4}>
                      <ATMGrid.Column>
                        <ATMField
                          name={`multiFlightInfo[${i}].flightDate`}
                          format="MM/DD/YYYY"
                          as={ATMInput}
                          control={control}
                          className="hidden"
                        />
                        <ATMField
                          key={`flightDate.multiFlightInfo_${elementId}`}
                          name={`multiFlightInfo[${i}].flightDate`}
                          label={
                            <LabelRequired>
                              {Lang.LBL_REQUEST_FLIGHT_DATE}
                            </LabelRequired>
                          }
                          format="MM/DD/YYYY"
                          className={styles.datePicker}
                          as={ATMDatePicker}
                          control={control}
                          defaultValue={fields[i]?.flightDate || ''}
                          error={get(
                            errors,
                            `multiFlightInfo[${i}].flightDate`,
                            false
                          )}
                          onChange={([_, { value }]) => {
                            control.setValue(
                              `multiFlightInfo[${i}].flightDate`,
                              value
                            );

                            return value;
                          }}
                          // minDate={Moment().startOf('day').toDate()}
                        />
                      </ATMGrid.Column>
                      <ATMGrid.Column>
                        <ATMField
                          name={`multiFlightInfo[${i}].departureTime`}
                          label={Lang.LBL_REQUEST_FLIGHT_DEPARTURE}
                          as={ATMInput}
                          defaultValue={fields[i]?.departureTime || ''}
                          type="number"
                          placeholder={Lang.LBL_ENTER}
                          control={control}
                        />
                      </ATMGrid.Column>
                      <ATMGrid.Column>
                        <ATMField
                          key={`arrivalTime.multiFlightInfo_${elementId}`}
                          name={`multiFlightInfo[${i}].arrivalTime`}
                          label={Lang.LBL_REQUEST_FLIGHT_ARRIVAL}
                          as={ATMInput}
                          type="number"
                          defaultValue={fields[i]?.arrivalTime || ''}
                          placeholder={Lang.LBL_ENTER}
                          control={control}
                        />
                      </ATMGrid.Column>
                      {!droneFlight && (
                        <>
                          <ATMGrid.Column>
                            <FieldReadOnly
                              label={Lang.LBL_REQUEST_FLIGHT_HOURS}
                            >
                              <ATMField
                                key={`flightHours.multiFlightInfo_${elementId}`}
                                name={`multiFlightInfo[${i}].flightHours`}
                                as={ATMInput}
                                type="number"
                                defaultValue={fields[i]?.flightHours || ''}
                                control={control}
                                placeholder={Lang.LBL_ENTER}
                              />
                            </FieldReadOnly>
                          </ATMGrid.Column>
                          <ATMGrid.Column>
                            <ATMField
                              key={`standByHours.multiFlightInfo_${elementId}`}
                              name={`multiFlightInfo[${i}].standByHours`}
                              label={Lang.LBL_REQUEST_FLIGHT_STANDBY_HOURS}
                              type="number"
                              as={ATMInput}
                              defaultValue={fields[i]?.standByHours || ''}
                              placeholder={Lang.LBL_ENTER}
                              control={control}
                            />
                          </ATMGrid.Column>
                        </>
                      )}
                      <ATMGrid.Column>
                        <ATMField
                          key={`flightNo.multiFlightInfo_${elementId}`}
                          name={`multiFlightInfo[${i}].flightNo`}
                          label={Lang.LBL_FLIGHT_NO}
                          as={ATMInput}
                          control={control}
                          readOnly
                        />
                      </ATMGrid.Column>
                    </ATMGrid>

                    <ATMHeader size="small" className="text-underline">
                      {Lang.TTL_DETAILS}
                    </ATMHeader>

                    <ATMGrid columns={4}>
                      <ATMGrid.Column>
                        <ATMField
                          key={`flight_type.multiFlightInfo_${elementId}`}
                          name={`multiFlightInfo[${i}].flightType`}
                          label={Lang.LBL_FLIGHT_TYPE}
                          as={ATMDropdown}
                          defaultValue={fields[i]?.flightType || ''}
                          placeholder={Lang.LBL_SELECT}
                          control={control}
                          options={flightTypeState.list.map((value) => ({
                            key: value.flightTypeId,
                            value: value.flightType,
                            text: value.flightType,
                          }))}
                          search
                          error={get(
                            errors,
                            `multiFlightInfo[${i}].flightType`,
                            false
                          )}
                          onChange={([_, { value }]) => {
                            if (
                              control.defaultValuesRef?.current?.passengerFlight
                            ) {
                              control.setValue(
                                'passengerFlight.flightType',
                                value
                              );
                            }
                            if (
                              control.defaultValuesRef?.current
                                ?.passengerContractorFlight
                            ) {
                              control.setValue(
                                'passengerContractorFlight.flightType',
                                value
                              );
                            }
                            return value;
                          }}
                          loading={
                            !flightTypeState.list.length &&
                            flightTypeStatus.fetching
                          }
                          disabled={
                            !flightTypeState.list.length &&
                            flightTypeStatus.fetching
                          }
                          selectOnBlur={false}
                          selection
                        />
                      </ATMGrid.Column>
                      <ATMGrid.Column>
                        <ATMField
                          name={`multiFlightInfo[${i}].operatorId`}
                          as={ATMInput}
                          control={control}
                          type="hidden"
                          className="hidden"
                        />
                        <ATMField
                          key={`operatorName.multiFlightInfo_${elementId}`}
                          name={`multiFlightInfo[${i}].operatorName`}
                          label={Lang.LBL_OPERATOR}
                          placeholder={Lang.LBL_SELECT}
                          as={ATMDropdown}
                          control={control}
                          defaultValue={
                            control.defaultValuesRef?.current?.multiFlightInfo[
                              i
                            ]?.operatorName ??
                            control.defaultValuesRef?.current
                              ?.constructionContractorFlight?.operatorName
                          }
                          options={state.list.map((dataItem) => ({
                            key: dataItem.operatorId,
                            value: dataItem.name,
                            text: dataItem.name,
                          }))}
                          onChange={([_, { value }]) => {
                            handleChange(value, i);
                            return value;
                          }}
                          search
                          error={get(
                            errors,
                            `multiFlightInfo[${i}].operatorName`,
                            false
                          )}
                          loading={!state.list.length && status.fetching}
                          disabled={!state.list.length && status.fetching}
                          selectOnBlur={false}
                          selection
                          clearable
                        />
                      </ATMGrid.Column>
                      <ATMGrid.Column>
                        <ATMField
                          key={`pilot.multiFlightInfo_${elementId}`}
                          name={`multiFlightInfo[${i}].pilot`}
                          label={Lang.LBL_PILOT_IN_COMMAND}
                          defaultValue={
                            control.defaultValuesRef?.current?.multiFlightInfo[
                              i
                            ]?.pilot || ''
                          }
                          placeholder={Lang.LBL_SELECT}
                          as={ATMDropdown}
                          options={
                            pilotInCommand[i]?.map((dataItem) => ({
                              key: dataItem.pilotId,
                              value: dataItem.name,
                              text: dataItem.name,
                            })) || []
                          }
                          control={control}
                          onChange={([_, { value }]) => value}
                          error={get(
                            errors,
                            `multiFlightInfo[${i}].pilot`,
                            false
                          )}
                          loading={!state.list.length && status.fetching}
                          selectOnBlur={false}
                          selection
                          clearable
                          search
                        />
                      </ATMGrid.Column>
                      <ATMGrid.Column>
                        <ATMField
                          key={`tailNumber.multiFlightInfo_${elementId}`}
                          name={`multiFlightInfo[${i}].tailNumber`}
                          defaultValue={
                            control.defaultValuesRef?.current?.multiFlightInfo[
                              i
                            ]?.tailNumber || ''
                          }
                          label={Lang.LBL_TAIL_NUMBER}
                          as={ATMDropdown}
                          options={
                            tailNumbers[i]?.map((dataItem) => ({
                              key: dataItem.tailId,
                              value: dataItem.tailNumber,
                              text: dataItem.tailNumber,
                            })) || []
                          }
                          placeholder={Lang.LBL_SELECT}
                          control={control}
                          onChange={([_, { value }]) => value}
                          error={get(
                            errors,
                            `multiFlightInfo[${i}].tailNumber`,
                            false
                          )}
                          loading={!state.list.length && status.fetching}
                          selectOnBlur={false}
                          selection
                          clearable
                          search
                        />
                      </ATMGrid.Column>
                      {!droneFlight && (
                        <>
                          <ATMGrid.Column>
                            <ATMField
                              key={`landingAssured.multiFlightInfo_${elementId}`}
                              name={`multiFlightInfo[${i}].landingAssured`}
                              label={Lang.LBL_LANDING_ASSURED}
                              as={ATMInput}
                              defaultValue={fields[i]?.landingAssured || ''}
                              placeholder={Lang.LBL_ENTER}
                              control={control}
                              error={get(
                                errors,
                                `multiFlightInfo[${i}].landingAssured`,
                                false
                              )}
                              maxLength={4}
                              onChange={([_, { value }]) => {
                                let temp = value.replace(/[^0-9]/, '');
                                if (temp === 2400) {
                                  temp = '0000';
                                } else if (temp > 2359 && temp !== 2400) {
                                  temp = 2359;
                                } else if (temp > 999 && temp % 100 > 60) {
                                  return parseInt(
                                    `${Math.floor(temp / 100)}${
                                      (temp % 100) - 60
                                    }`,
                                    10
                                  );
                                }

                                return temp;
                              }}
                              onBlur={(_, value) => {
                                const temp = value ? parseInt(value, 10) : 0;
                                /* eslint no-else-return: "error" */
                                if (temp === 2400 || temp === 0) {
                                  return '0000';
                                } else if (temp > 2359 && temp !== 2400) {
                                  return 2359;
                                } else if (temp > 999 && temp % 100 > 60) {
                                  return `${Math.floor(temp / 100) + 1}${
                                    (temp % 100) - 60
                                  }`;
                                } else if (temp > 999 && temp % 100 === 60) {
                                  return `${Math.floor(temp / 100) + 1}00`;
                                } else if (temp > 999) {
                                  return temp;
                                } else if (temp > 99 && temp % 100 > 60) {
                                  return Math.floor(temp / 100) + 1 === 10
                                    ? `${Math.floor(temp / 100) + 1}${
                                        (temp % 100) - 60
                                      }`
                                    : `0${Math.floor(temp / 100) + 1}${
                                        (temp % 100) - 60
                                      }`;
                                } else if (temp > 99 && temp % 100 === 60) {
                                  return `0${Math.floor(temp / 100) + 1}00`;
                                } else if (temp > 99) {
                                  return `0${temp}`;
                                } else if (temp < 100 && temp > 60) {
                                  return `00${(temp % 100) - 60}`;
                                } else if (temp === 60 || temp === 100) {
                                  return '0100';
                                } else if (temp < 60 && temp > 9) {
                                  return `00${temp}`;
                                } else if (temp < 10) {
                                  return `000${temp}`;
                                }

                                return temp;
                              }}
                            />
                          </ATMGrid.Column>

                          <ATMGrid.Column>
                            <ATMField
                              key={`flightCost.multiFlightInfo_${elementId}`}
                              name={`multiFlightInfo[${i}].flightCost`}
                              label={Lang.LBL_FLIGHT_COST}
                              as={ATMInput}
                              defaultValue={fields[i]?.flightCost || ''}
                              placeholder={Lang.LBL_ENTER}
                              control={control}
                              onBlur={(_, value) => {
                                if (value) {
                                  const temp = value
                                    .replace('$', '')
                                    .replace(',', '')
                                    .replace(/[^0-9.]+/, '');

                                  const amount = parseFloat(temp);

                                  return !isNaN(amount)
                                    ? `$${parseFloat(temp)
                                        .toFixed(2)
                                        .replace(/\d(?=(\d{3})+\.)/g, '$&,')}`
                                    : '';
                                }
                                return '';
                              }}
                            />
                          </ATMGrid.Column>
                        </>
                      )}
                      <ATMGrid.Column>
                        {
                          !FlightCategoryDrone.includes(
                            RequestFlightPathName[
                              get(
                                control,
                                'defaultValuesRef.current.request.flightFlag'
                              ) ?? 1
                            ]
                          )
                        }
                      </ATMGrid.Column>
                    </ATMGrid>
                    <ATMTable.Cell textAlign="right">
                      {fields.length > 0 && (
                        <ATMButton
                          icon="trash alternate outline"
                          iconcolor="black"
                          type="button"
                          onClick={() => {
                            if (item.multiFlightInfoId) {
                              setMultiFlightItem(item.flightNo);
                            } else {
                              const indexToRemove = fields.findIndex(
                                (f) => f.id === item.id
                              );
                              if (indexToRemove !== -1) {
                                remove(indexToRemove);
                              }
                            }
                          }}
                        />
                      )}
                    </ATMTable.Cell>
                  </ATMTable.Row>
                );
              })}
            </ATMTable.Body>
            {/* )} */}
            <ATMTable.Footer>
              <ATMTable.Row>
                <ATMTable.HeaderCell colSpan={4}>
                  <ATMButton
                    secondary
                    icon="plus"
                    type="button"
                    onClick={handleAdd}
                    disabled={fields.length >= limit}
                    content={Lang.LBL_ADD}
                  />
                </ATMTable.HeaderCell>
              </ATMTable.Row>
            </ATMTable.Footer>
          </ATMTable>
          <Confirm
            open={deleteMultiFlightItem !== undefined}
            size="tiny"
            onCancel={() => setMultiFlightItem(undefined)}
            header={Lang.TTL_CONFIRM_DELETE}
            content={Lang.formatString(
              Lang.MSG_MULTIFLIGHT_DETAIL_CONFIRM_DELETE,
              deleteMultiFlightItem ?? ''
            )}
            onConfirm={() => {
              if (deleteMultiFlightItem !== undefined) {
                remove(
                  fields.findIndex(
                    (value) => value.flightNo === deleteMultiFlightItem
                  )
                );
                ToastSuccess(
                  Lang.formatString(
                    Lang.MSG_MULTIFLIGHT_DELETE_SUCCESS,
                    deleteMultiFlightItem ?? ''
                  )
                );
                setMultiFlightItem(undefined);
              }
            }}
            confirmText={Lang.LBL_YES_DELETE}
          />
        </ATMGrid.Column>
      </ATMGrid>
    </>
  );
};

export default FlightRequestMultiFlightInfo;
